import React, { FunctionComponent } from 'react';
import { PolioDialogTabs } from './MainDialog/PolioDialogTabs';
import { FormikProvider } from 'formik';
import { useCampaignFormState } from './hooks/useCampaignFormState';
import { useCampaignTabs } from './hooks/useCampaignTabs';
import { Form } from '../../components/Form';
import TopBar from 'Iaso/components/nav/TopBarComponent';
import { LoadingSpinner, useGoBack, useSafeIntl } from 'bluesquare-components';
import MESSAGES from '../../constants/messages';
import { WarningModal } from './MainDialog/WarningModal/WarningModal';
import { CampaignHistoryIconButton } from './CampaignHistory/CampaignHistoryIconButton';
import { baseUrls } from '../../constants/urls';
import { useParamsObject } from 'Iaso/routing/hooks/useParamsObject';
import { Box, Button, Grid } from '@mui/material';
import { useStyles } from '../../styles/theme';
import { Campaign } from '../../constants/types';

const useTitle = (
    campaignId: string | undefined,
    selectedCampaign?: Campaign,
): string => {
    const { formatMessage } = useSafeIntl();
    if (selectedCampaign) {
        return formatMessage(MESSAGES.campaignDetail, {
            obrName: `${selectedCampaign.obr_name}`,
        });
    } else if (campaignId) {
        return formatMessage(MESSAGES.campaignDetail, {
            obrName: `${campaignId}`,
        });
    }
    return formatMessage(MESSAGES.createCampaign);
};

export const CampaignDetails: FunctionComponent = () => {
    const classes: Record<string, string> = useStyles();
    const params = useParamsObject(baseUrls.campaignDetails);
    const { campaignId } = params;
    const { formatMessage } = useSafeIntl();
    const goBack = useGoBack();
    const {
        formik,
        isScopeWarningOpen,
        closeWarning,
        scopeWarningTitle,
        scopeWarningBody,
        warningDataTestId,
        handleConfirm,
        handleClose: handleCancel,
        isSaving,
        selectedCampaign,
        isFetching,
        saveDisabled,
    } = useCampaignFormState({
        campaignId,
    });

    const { tabs, ActiveForm, handleChangeTab, selectedTab } = useCampaignTabs({
        formik,
        selectedCampaign,
    });
    const title = useTitle(campaignId, selectedCampaign);

    return (
        <>
            <TopBar title={title} displayBackButton goBack={goBack} />
            {(isFetching || isSaving) && <LoadingSpinner absolute />}
            <WarningModal
                title={scopeWarningTitle}
                body={scopeWarningBody}
                open={isScopeWarningOpen}
                closeDialog={closeWarning}
                onConfirm={() => formik.handleSubmit()}
                dataTestId={warningDataTestId}
            />
            <Box className={classes.containerFullHeightPadded}>
                <CampaignHistoryIconButton
                    selectedCampaign={selectedCampaign}
                />
                <PolioDialogTabs
                    tabs={tabs}
                    selectedTab={selectedTab}
                    handleChange={handleChangeTab}
                />
                <FormikProvider value={formik}>
                    <Form isModal={false}>
                        <ActiveForm />
                    </Form>
                </FormikProvider>
                <Grid container spacing={2} justifyContent="flex-end" mt={2}>
                    <Box mr={2}>
                        <Button
                            onClick={handleCancel}
                            color="primary"
                            disabled={isSaving}
                        >
                            {formatMessage(MESSAGES.cancel)}
                        </Button>
                    </Box>
                    <Button
                        onClick={handleConfirm}
                        color="primary"
                        variant="contained"
                        autoFocus
                        disabled={saveDisabled}
                    >
                        {formatMessage(MESSAGES.save)}
                    </Button>
                </Grid>
            </Box>
        </>
    );
};
